import { useEffect, useMemo } from 'react';

import Layout from 'components/Layout';
import useFirePageOpen from 'hooks/useFirePageOpen';

import { useIsTranslationsFetched } from '@picsart/rc/services/localization';
import { usePulseSettings } from '@pulse/react';
import JSONLinkedData from 'components/JSONLinkedData';
import { SchemaType } from 'components/JSONLinkedData/types';

import useMobileView from 'hooks/useMobileView';
import usePageSlug from 'hooks/usePageSlug';
import useRedirectToCreate from 'hooks/useRedirectToCreate';

import usePageDataStore from 'stores/pageDataStore';
import noIndexPages from '../constants/noIndexPages';
import LandingComponents from './LandingComponents';
import { LandingProps } from './types';

function Landing({ screenName, pageData, isLocalized, languages }: LandingProps) {
  const { isMobile } = useMobileView();
  useRedirectToCreate();
  const isTranslationsFetched = useIsTranslationsFetched();
  const { slug: pagePath } = usePageSlug();
  const isColorsPage = usePageDataStore(store => store.pageData?.data.slug.startsWith('colors'));
  const containVideo = usePageDataStore(store => store.pageData?.data?.containVideo);

  const { settings: webToMobile, loading } = usePulseSettings<boolean>({
    name: 'use_feature_web_to_mobile',
    tag: 'webtomobile',
  });

  useEffect(() => {
    if (!loading && webToMobile) {
      localStorage.setItem('webToMobile', '1');
    }
    // eslint-disable-next-line
    // @ts-ignore
    window.DD_RUM?.addFeatureFlagEvaluation('webToMobile', webToMobile);
  }, [loading, webToMobile]);

  useEffect(() => {
    if (pagePath.replace('/', '').trim() === 'home') {
      window.location.href = '/';
    }
  }, [pagePath]);

  useFirePageOpen(screenName, pageData?.customEventName, pageData?.category);

  const isPageNoIndex = useMemo(() => {
    return noIndexPages.some(page => page === pageData?.slug);
  }, [noIndexPages, pageData]);
  return isLocalized != null && pageData && isTranslationsFetched ? (
    <>
      <JSONLinkedData
        data={{
          name: pageData.name,
          description: pageData?.seoSettings?.metaDescription,
          url: pageData.slug,
        }}
        type={SchemaType.WebPage}
      />
      {isColorsPage && <JSONLinkedData data={pageData} type={SchemaType.Article} />}
      {containVideo && <JSONLinkedData data={pageData} type={SchemaType.VideoObject} />}
      <main>
        <Layout
          {...{
            ...pageData?.seoSettings,
            miniNavigation: pageData?.miniNavigation, // note that miniNavigation and customFooter are only used by apps-jpcpg page
            customFooter: pageData?.customFooter,
            footerRelevantLinks: pageData?.footerRelevantLinks,
            isLocalized,
            hreflangLanguages: languages,
            isMobile,
            isLandingOrHub: true,
            noIndex: isPageNoIndex,
            hasDefaultBreadcrumb: !pageData?.colorData && pageData?.slug !== 'colors',
            hideHeaderAndFooterNavigations: pageData?.hideHeaderAndFooterNavigations,
            logoBehavior: pageData?.logoBehavior,
            imageSrc: pageData?.imgSrc,
          }}
        >
          <LandingComponents pageData={pageData} />
        </Layout>
      </main>
    </>
  ) : null;
}

export default Landing;
